import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { css } from "@emotion/react";
import { ContentfulImage } from "../../../components/_shared-components/image-related/ContentfulImage";
import React from "react";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { SimpleAspectRatio } from "../../../components/_shared-components/SimpleAspectRatio";
import { CSS_MQ } from "../../../styles/breakpoints";
import { ImageWithFocalPoint } from "../../../components/_shared-components/image-related/ImageWithFocalPoint";
import { SwarovskiOptikVideoPlayer } from "../../../components/VideoComponent/VideoComponent";
import { divStyles } from "../styles";
import { PdpGalleryWithLightboxProps } from "../PDP_Lightbox/use-pdp-gallery-with-lightbox";
import cn from "classnames";

export const PDPScrollingGalleryElements: ReactTFC<{
  galleryWithLightbox: NonNullable<PdpGalleryWithLightboxProps>;
}> = props => {
  const {
    galleryConfig: { scrollingGalleryElements },
    openModal,
  } = props.galleryWithLightbox;

  return (
    <div css={divStyles} className={scrollingGalleryElements.length <= 1 ? "one-image" : "multiple-images"}>
      {scrollingGalleryElements.map(elem => {
        if (elem.__typename === "UtilityVideo") {
          return (
            <SimpleAspectRatio
              atLeast={"md"}
              key={elem.video.url}
              as={"button"}
              onClick={() => openModal(elem.video.url)}
              css={[aspectRatioStyle, wrapperStyles]}
            >
              <SwarovskiOptikVideoPlayer
                video={elem.video}
                subtitles={elem.subtitles}
                sys={{ id: "" }}
                portraitMode={true}
                withPlayerUI={false}
                resolutionPickerStrategy="ceil"
                sharedVideoProps={{
                  muted: true,
                  playsInline: true,
                  autoPlay: true,
                  loop: true,
                  preload: "auto",
                }}
                mode={"fill"}
              />
            </SimpleAspectRatio>
          );
        }

        if (elem.focalPoint) {
          return (
            <button
              className={"with-focal-point"}
              key={elem.url}
              onClick={() => openModal(elem.url)}
              css={[wrapperStyles]}
            >
              <ImageWithFocalPoint
                assetId={elem.sys.id}
                mode="v2"
                requestedWidth={600}
                focalPoint={elem.focalPoint}
                css={css`
                  overflow: hidden;
                `}
                image={{
                  url: elem.url,
                  title: elem.title,
                  description: elem.description,
                  width: elem.width,
                  height: elem.height,
                }}
                lazyLoaded={"lazy"}
                attachPictureStyles={css`
                  display: block;
                  height: 100%;
                  width: 100%;
                `}
              />
            </button>
          );
        } else {
          return (
            <button key={elem.url} onClick={() => openModal(elem.url)} css={wrapperStyles} className={"no-focal-point"}>
              <ContentfulImage
                assetId={elem.sys.id}
                contentfulLink={{ url: elem.url, noTransparentBackground: false, requestedWidth: 600 }}
                lazyLoaded={"lazy"}
                sizes={"700px"}
                higherDPI={[0.5, 2]}
                className={cn({ "is-lifestyle-asset": elem.isLifestyleAsset })}
                attachPictureStyles={css`
                  ${CSS_MQ.atLeastMd} {
                    border: 1px solid ${CSS_COLORS.Grey0};
                    border-radius: 2px;
                  }

                  display: block;
                  height: 100%;
                  width: 100%;

                  min-height: 240px;

                  &:not(.is-lifestyle-asset) {
                    background-color: ${CSS_COLORS.GreenWhite};
                  }
                `}
                css={css`
                  width: 100%;
                  height: 100%;
                  object-fit: contain;
                  display: block;
                `}
              />
            </button>
          );
        }
      })}
    </div>
  );
};

const aspectRatioStyle = css`
  ${CSS_MQ.atMostSm} {
    width: calc(100vw - 40px);
    height: var(--height);
  }
`;

const wrapperStyles = css`
  ${CSS_MQ.atMostSm} {
    position: relative;
    display: inline-block;
  }

  ${CSS_MQ.atLeastMd} {
    > * {
      border: 1px solid ${CSS_COLORS.Grey0};
      border-radius: 2px;
      overflow: hidden;
    }

    &:focus {
      outline: none !important;
      > * {
        border: 1px solid ${CSS_COLORS.Green};
      }
    }
  }
`;
